import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import {
  BULK_RESET_STUDENT_PASSWORDS,
  BULK_UPDATE_STUDENTS,
  DISABLE_STUDENTS,
  ENABLE_STUDENTS,
  UPDATE_STUDENT_STATUS,
  EXPORT_STUDENTS,
  ENROLL_STUDENTS_IN_CALENDAR,
  DEENROLL_STUDENTS,
} from '@/graphql/mutations/studentOverview';
import type {
  BulkResetStudentPasswordsInput,
  BulkResetStudentPasswordsMutation,
  BulkResetStudentPasswordsMutationVariables,
  BulkUpdateStudentsInput,
  BulkUpdateStudentsMutation,
  BulkUpdateStudentsMutationVariables,
  DeenrollStudentsInput,
  DeenrollStudentsMutation,
  DeenrollStudentsMutationVariables,
  DisableStudentsInput,
  DisableStudentsMutation,
  DisableStudentsMutationVariables,
  EnableStudentsInput,
  EnableStudentsMutation,
  EnableStudentsMutationVariables,
  EnrollStudentsInCalendarInput,
  EnrollStudentsInCalendarMutation,
  EnrollStudentsInCalendarMutationVariables,
  ExportStudentsInput,
  ExportStudentsMutation,
  ExportStudentsMutationVariables,
  UpdateStudentStatusInput,
  UpdateStudentStatusMutation,
  UpdateStudentStatusMutationVariables,
} from '@/types/operations';

/** Per-student outcome summary returned by bulkResetStudentPasswords. */
export type BulkResetStudentPasswordsResult = BulkResetStudentPasswordsMutation['bulkResetStudentPasswords'];

export function useStudentOverviewMutations() {
  const toast = useToast();

  const bulkUpdateMutation = useMutation<BulkUpdateStudentsMutation, BulkUpdateStudentsMutationVariables>(BULK_UPDATE_STUDENTS);
  const disableMutation = useMutation<DisableStudentsMutation, DisableStudentsMutationVariables>(DISABLE_STUDENTS);
  const enableMutation = useMutation<EnableStudentsMutation, EnableStudentsMutationVariables>(ENABLE_STUDENTS);
  const updateStatusMutation = useMutation<UpdateStudentStatusMutation, UpdateStudentStatusMutationVariables>(UPDATE_STUDENT_STATUS);
  const exportMutation = useMutation<ExportStudentsMutation, ExportStudentsMutationVariables>(EXPORT_STUDENTS);
  const enrollMutation = useMutation<EnrollStudentsInCalendarMutation, EnrollStudentsInCalendarMutationVariables>(ENROLL_STUDENTS_IN_CALENDAR);
  const deenrollMutation = useMutation<DeenrollStudentsMutation, DeenrollStudentsMutationVariables>(DEENROLL_STUDENTS);
  const resetPasswordsMutation = useMutation<BulkResetStudentPasswordsMutation, BulkResetStudentPasswordsMutationVariables>(BULK_RESET_STUDENT_PASSWORDS);

  async function bulkUpdateStudents(input: BulkUpdateStudentsInput): Promise<boolean> {
    try {
      const result = await bulkUpdateMutation.mutate({ input });
      const response = result?.data?.bulkUpdateStudents;
      if (response?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: response.message, life: 3000 });
        return true;
      }
      toast.add({ severity: 'warn', summary: 'Failed', detail: response?.message || 'Bulk update failed', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'Bulk update failed', life: 4000 });
      return false;
    }
  }

  async function disableStudents(input: DisableStudentsInput): Promise<boolean> {
    try {
      const result = await disableMutation.mutate({ input });
      const response = result?.data?.disableStudents;
      if (response?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: response.message, life: 3000 });
        return true;
      }
      toast.add({ severity: 'warn', summary: 'Failed', detail: response?.message || 'Disable failed', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'Disable failed', life: 4000 });
      return false;
    }
  }

  async function enableStudents(input: EnableStudentsInput): Promise<boolean> {
    try {
      const result = await enableMutation.mutate({ input });
      const response = result?.data?.enableStudents;
      if (response?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: response.message, life: 3000 });
        return true;
      }
      toast.add({ severity: 'warn', summary: 'Failed', detail: response?.message || 'Enable failed', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'Enable failed', life: 4000 });
      return false;
    }
  }

  async function updateStudentStatus(input: UpdateStudentStatusInput): Promise<boolean> {
    try {
      const normalizedInput = { ...input, status: input.status.toLowerCase() };
      const result = await updateStatusMutation.mutate({ input: normalizedInput });
      const response = result?.data?.updateStudentStatus;
      if (response?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: response.message, life: 3000 });
        return true;
      }
      toast.add({ severity: 'warn', summary: 'Failed', detail: response?.message || 'Status update failed', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'Status update failed', life: 4000 });
      return false;
    }
  }

  async function exportStudents(input: ExportStudentsInput): Promise<ExportStudentsMutation['exportStudents'] | null> {
    try {
      const cleanInput = Object.fromEntries(
        Object.entries(input).filter(([, v]) => v != null && v !== ''),
      ) as ExportStudentsInput;
      const result = await exportMutation.mutate({ input: cleanInput });
      const response = result?.data?.exportStudents;
      if (response) {
        toast.add({ severity: 'info', summary: 'Export Queued', detail: 'Student export is being generated. Check the Downloads panel for progress.', life: 5000 });
        return response;
      }
      toast.add({ severity: 'warn', summary: 'Failed', detail: 'Export could not be started', life: 3000 });
      return null;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'Export failed', life: 4000 });
      return null;
    }
  }

  async function enrollStudentsInCalendar(input: EnrollStudentsInCalendarInput): Promise<boolean> {
    try {
      const result = await enrollMutation.mutate({ input });
      const response = result?.data?.enrollStudentsInCalendar;
      if (response?.success) {
        toast.add({ severity: 'success', summary: 'Enrolled', detail: response.message, life: 5000 });
        return true;
      }
      toast.add({ severity: 'warn', summary: 'Failed', detail: response?.message || 'Enrollment failed', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'Enrollment failed', life: 4000 });
      return false;
    }
  }

  /**
   * Resets every selected student to the surname default. Resolves with the
   * per-student summary (so the caller can name the failures) or null when the
   * request itself failed. Partial success is a warn toast, not an error.
   */
  async function bulkResetStudentPasswords(input: BulkResetStudentPasswordsInput): Promise<BulkResetStudentPasswordsResult | null> {
    try {
      const result = await resetPasswordsMutation.mutate({ input });
      const response = result?.data?.bulkResetStudentPasswords;
      if (!response) {
        toast.add({ severity: 'warn', summary: 'Failed', detail: 'Password reset failed', life: 3000 });
        return null;
      }
      if (response.failed_count === 0) {
        toast.add({ severity: 'success', summary: 'Passwords reset', detail: response.message, life: 5000 });
      } else if (response.reset_count > 0) {
        toast.add({ severity: 'warn', summary: 'Partially reset', detail: response.message, life: 6000 });
      } else {
        toast.add({ severity: 'error', summary: 'No passwords reset', detail: response.message, life: 6000 });
      }
      return response;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'Password reset failed', life: 4000 });
      return null;
    }
  }

  return {
    bulkUpdateStudents,
    bulkUpdating: bulkUpdateMutation.loading,
    disableStudents,
    disabling: disableMutation.loading,
    enableStudents,
    enabling: enableMutation.loading,
    updateStudentStatus,
    updatingStatus: updateStatusMutation.loading,
    exportStudents,
    exporting: exportMutation.loading,
    enrollStudentsInCalendar,
    enrolling: enrollMutation.loading,
    deenrollStudents: async(input: DeenrollStudentsInput): Promise<boolean> => {
      try {
        const result = await deenrollMutation.mutate({ input });
        const response = result?.data?.deenrollStudents;
        if (response?.success) {
          toast.add({ severity: 'success', summary: 'De-enrolled', detail: response.message, life: 5000 });
          return true;
        }
        toast.add({ severity: 'warn', summary: 'Failed', detail: response?.message || 'De-enrollment failed', life: 3000 });
        return false;
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'De-enrollment failed', life: 4000 });
        return false;
      }
    },
    deenrolling: deenrollMutation.loading,
    bulkResetStudentPasswords,
    resettingPasswords: resetPasswordsMutation.loading,
  };
}
